/**
 * MiniMapDisplay — a compact navigational mini-map shown in the HUD overlay.
 *
 * Renders visited maze cells at a reduced scale with color-coded elements:
 *  - Visited paths (dim gray)
 *  - Visited walls (dark gray)
 *  - Hazard cells: water (blue), lava (orange), poison (green), spikes (light gray)
 *  - Player position (white dot)
 *  - Exit position (gold dot, only when compass collected)
 *
 * Positioned bottom-right, above the compass widget. Toggle via TAB key.
 */

import Phaser from 'phaser';
import { SCENE_GAME } from '../utils/constants.js';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  BORDER_MARGIN,
  TILE_SIZE,
  MINIMAP_WIDTH,
  MINIMAP_HEIGHT,
  MINIMAP_MARGIN,
  MINIMAP_BG_ALPHA,
  MINIMAP_BORDER_COLOR,
  MINIMAP_BORDER_ALPHA,
  MINIMAP_WALL_COLOR,
  MINIMAP_PATH_COLOR,
  MINIMAP_PLAYER_COLOR,
  MINIMAP_EXIT_COLOR,
  MINIMAP_WATER_COLOR,
  MINIMAP_LAVA_COLOR,
  MINIMAP_POISON_COLOR,
  MINIMAP_SPIKE_COLOR,
  MINIMAP_PLAYER_DOT_SIZE,
  MINIMAP_EXIT_DOT_SIZE,
} from '../config/game-config.js';

export default class MiniMapDisplay {
  /**
   * @param {Phaser.Scene} scene  The HudScene instance.
   */
  constructor(scene) {
    /** @type {Phaser.Scene} */
    this.scene = scene;

    /** @type {boolean} */
    this._visible = false;

    // Panel position: bottom-right, above the compass (compass center is at ~745, 545)
    // Mini-map right edge aligned with border, bottom edge above compass area.
    this._x = GAME_WIDTH - BORDER_MARGIN - MINIMAP_MARGIN - MINIMAP_WIDTH;
    this._y = GAME_HEIGHT - BORDER_MARGIN - MINIMAP_MARGIN - MINIMAP_HEIGHT - 60;

    // Background panel
    this._bgGraphics = scene.add.graphics();
    this._bgGraphics.setDepth(190);
    this._bgGraphics.setAlpha(0);

    // Map content graphics
    this._mapGraphics = scene.add.graphics();
    this._mapGraphics.setDepth(191);
    this._mapGraphics.setAlpha(0);

    // Border graphics
    this._borderGraphics = scene.add.graphics();
    this._borderGraphics.setDepth(192);
    this._borderGraphics.setAlpha(0);

    // "MAP" label
    this._label = scene.add.text(
      this._x + MINIMAP_WIDTH / 2,
      this._y - 8,
      'MAP',
      {
        fontFamily: 'monospace',
        fontSize: '8px',
        color: '#888888',
        align: 'center',
      },
    ).setOrigin(0.5, 1).setAlpha(0).setDepth(192);

    // Draw the static border once
    this._drawBorder();

    // Cache for current maze dimensions (set on first update)
    this._gridWidth = 0;
    this._gridHeight = 0;
    this._scaleX = 1;
    this._scaleY = 1;
  }

  /** Show the mini-map. */
  show() {
    this._visible = true;
    this._bgGraphics.setAlpha(1);
    this._mapGraphics.setAlpha(1);
    this._borderGraphics.setAlpha(1);
    this._label.setAlpha(1);
    // Force an immediate redraw
    this._redraw();
  }

  /** Hide the mini-map. */
  hide() {
    this._visible = false;
    this._bgGraphics.setAlpha(0);
    this._mapGraphics.setAlpha(0);
    this._borderGraphics.setAlpha(0);
    this._label.setAlpha(0);
  }

  /** Toggle visibility. */
  toggle() {
    if (this._visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  /** @returns {boolean} Whether the mini-map is currently visible. */
  isVisible() {
    return this._visible;
  }

  /**
   * Update the mini-map after the player moves.
   * Reads current state directly from GameScene.
   */
  update() {
    if (!this._visible) return;
    this._redraw();
  }

  /**
   * Full redraw of mini-map content. Reads maze state from the GameScene.
   * @private
   */
  _redraw() {
    const gameScene = this.scene.scene.get(SCENE_GAME);
    if (!gameScene || !gameScene.mazeGrid || !gameScene.visited) return;

    const grid = gameScene.mazeGrid;
    const visited = gameScene.visited;
    const gridW = gameScene.mazeGridWidth;
    const gridH = gameScene.mazeGridHeight;

    // Recalculate scale if maze dimensions changed
    if (gridW !== this._gridWidth || gridH !== this._gridHeight) {
      this._gridWidth = gridW;
      this._gridHeight = gridH;
      this._scaleX = MINIMAP_WIDTH / gridW;
      this._scaleY = MINIMAP_HEIGHT / gridH;
    }

    const sx = this._scaleX;
    const sy = this._scaleY;
    const ox = this._x;   // offset x
    const oy = this._y;   // offset y

    // Gather hazard cell sets
    const waterCells = gameScene.waterSystem ? gameScene.waterSystem.waterCells : null;
    const lavaCells = gameScene.lavaSystem ? gameScene.lavaSystem.lavaCells : null;
    const poisonCells = gameScene.poisonSystem ? gameScene.poisonSystem.poisonCells : null;
    const spikeCells = gameScene.spikeSystem ? gameScene.spikeSystem.spikeCells : null;

    // Draw background
    this._bgGraphics.clear();
    this._bgGraphics.fillStyle(0x000000, MINIMAP_BG_ALPHA);
    this._bgGraphics.fillRect(ox, oy, MINIMAP_WIDTH, MINIMAP_HEIGHT);

    // Draw map content
    const g = this._mapGraphics;
    g.clear();

    // Render each visited cell
    for (let r = 0; r < gridH; r++) {
      for (let c = 0; c < gridW; c++) {
        if (!visited[r][c]) continue; // Not yet seen — skip (fog)

        const px = ox + c * sx;
        const py = oy + r * sy;
        const pw = Math.max(sx, 1);
        const ph = Math.max(sy, 1);

        // Determine cell color
        const cellKey = `${c},${r}`;
        const isWall = grid[r][c] === 1;

        if (isWall) {
          g.fillStyle(MINIMAP_WALL_COLOR, 0.8);
        } else if (lavaCells && lavaCells.has(cellKey)) {
          g.fillStyle(MINIMAP_LAVA_COLOR, 0.9);
        } else if (waterCells && waterCells.has(cellKey)) {
          g.fillStyle(MINIMAP_WATER_COLOR, 0.9);
        } else if (poisonCells && poisonCells.has(cellKey)) {
          g.fillStyle(MINIMAP_POISON_COLOR, 0.9);
        } else if (spikeCells && spikeCells.has(cellKey)) {
          g.fillStyle(MINIMAP_SPIKE_COLOR, 0.7);
        } else {
          g.fillStyle(MINIMAP_PATH_COLOR, 0.5);
        }

        g.fillRect(px, py, pw, ph);
      }
    }

    // Draw exit dot (only when compass is collected)
    if (gameScene._hasCompass && gameScene.exitPos) {
      const ex = ox + gameScene.exitPos.col * sx + sx / 2;
      const ey = oy + gameScene.exitPos.row * sy + sy / 2;
      g.fillStyle(MINIMAP_EXIT_COLOR, 1);
      g.fillCircle(ex, ey, MINIMAP_EXIT_DOT_SIZE);
    }

    // Draw player dot
    if (gameScene.player) {
      const playerPos = gameScene.player.getGridPosition();
      const ppx = ox + playerPos.col * sx + sx / 2;
      const ppy = oy + playerPos.row * sy + sy / 2;
      g.fillStyle(MINIMAP_PLAYER_COLOR, 1);
      g.fillCircle(ppx, ppy, MINIMAP_PLAYER_DOT_SIZE);
    }
  }

  /**
   * Draw the static border around the mini-map panel.
   * @private
   */
  _drawBorder() {
    const g = this._borderGraphics;
    g.clear();
    g.lineStyle(1, MINIMAP_BORDER_COLOR, MINIMAP_BORDER_ALPHA);
    g.strokeRect(this._x - 1, this._y - 1, MINIMAP_WIDTH + 2, MINIMAP_HEIGHT + 2);
  }

  /** Destroy all graphics objects. */
  destroy() {
    if (this._bgGraphics) {
      this._bgGraphics.destroy();
      this._bgGraphics = null;
    }
    if (this._mapGraphics) {
      this._mapGraphics.destroy();
      this._mapGraphics = null;
    }
    if (this._borderGraphics) {
      this._borderGraphics.destroy();
      this._borderGraphics = null;
    }
    if (this._label) {
      this._label.destroy();
      this._label = null;
    }
  }
}
